import { supabase } from '@/lib/supabase';
import { isAdmin, type OrgRole } from '@/shared/org';
import {
  ShareError,
  type AccessLevel,
  type AddOutcome,
  type AccessRequest,
  type ShareAccess,
  type ShareAdapter,
  type ShareGrant,
  type SharePrincipal,
  type ShareSubject,
  type PublicAccess,
} from '@/shared/sharing';
import { setSheetPublic } from './publicSheet';
import { editorPathFor } from './templates';

/**
 * Sharing one Folders node, for the dialog already ported from TMS.
 *
 * `resource_type` is `folders_node` and `resource_id` is the node's uuid — the
 * same uuid that is in the address bar, which is what makes "copy the link and
 * grant access" one idea instead of two.
 *
 * A share on a FOLDER reaches everything inside it: `private.folders_shared_level`
 * walks the ancestors. That is deliberate and is why the dialog is offered on
 * folders too.
 *
 * Invitations and direct grants are created, changed and accepted by the
 * Worker. The browser never writes a resource_shares row directly.
 */

export const FOLDERS_RESOURCE_TYPE = 'folders_node';

interface DirectoryEntry {
  userId: string;
  fullName: string | null;
  email: string;
  role: OrgRole;
}

async function loadDirectory(orgId: string): Promise<DirectoryEntry[]> {
  const { data, error } = await supabase.rpc('org_directory', { p_org_id: orgId });
  if (error) throw new ShareError('unknown', error.message);

  return (data ?? []).map((row) => ({
    userId: row.user_id,
    fullName: row.full_name,
    email: row.email ?? '',
    role: row.role as OrgRole,
  }));
}

function toPrincipal(entry: DirectoryEntry): SharePrincipal {
  return {
    userId: entry.userId,
    name: entry.fullName,
    email: entry.email,
    role: entry.role,
  };
}

interface Context {
  /** The signed-in user, for deciding which grants they may change. */
  readonly userId: string;
  readonly role: OrgRole;
  /** Whether sharing an unknown email may create an invitation. */
  readonly invitations?: boolean;
}

export function createFoldersShareAdapter(context: Context): ShareAdapter {
  const assertResource = (subject: ShareSubject) => {
    if (subject.kind !== 'resource') {
      throw new ShareError('unknown', 'the folder adapter only shares resources');
    }
    return subject;
  };

  return {
    async load(subject, signal): Promise<ShareAccess> {
      const resource = assertResource(subject);

      const [directory, shares, publicDocument, node, requests] = await Promise.all([
        loadDirectory(resource.orgId),
        supabase
          .from('resource_shares')
          .select('user_id, recipient_email, level, created_by')
          .eq('org_id', resource.orgId)
          .eq('resource_type', resource.resourceType)
          .eq('resource_id', resource.resourceId)
          .abortSignal(signal),
        resource.resourceType === FOLDERS_RESOURCE_TYPE
          ? supabase.from('sheet_documents').select('is_public').eq('id', resource.resourceId).maybeSingle()
          : Promise.resolve({ data: null }),
        resource.resourceType === FOLDERS_RESOURCE_TYPE
          ? supabase.from('folders_nodes').select('doc_type').eq('id', resource.resourceId).eq('org_id', resource.orgId).maybeSingle()
          : Promise.resolve({ data: null }),
        resource.resourceType === FOLDERS_RESOURCE_TYPE
          ? supabase.from('access_requests').select('id, requester_email, created_at').eq('node_id', resource.resourceId).order('created_at')
          : Promise.resolve({ data: null }),
      ]);

      if (shares.error) throw new ShareError('unknown', shares.error.message);

      const byId = new Map(directory.map((entry) => [entry.userId, entry]));

      const grants: ShareGrant[] = (shares.data ?? []).map((row) => {
        const entry = byId.get(row.user_id);
        return {
          userId: row.user_id,
          // An outside collaborator is not in the organization's directory, so
          // there is no name to show and the dialog labels them as such.
          name: entry?.fullName ?? null,
          // Resource shares may target people outside the organization, who
          // intentionally do not appear in `org_directory`. Keep their email
          // with the share so the owner can identify and manage the grant.
          email: entry?.email ?? row.recipient_email ?? '',
          role: entry?.role ?? null,
          level: row.level as AccessLevel,
          // Presentation only — the Worker re-checks ownership and Folders edit
          // capability before changing this grant.
          canManage: isAdmin(context.role) || row.created_by === context.userId,
        };
      });

      const granted = new Set(grants.map((grant) => grant.userId));

      // Owners and admins bypass `module_permissions`, so they already have the
      // document whether or not anyone shared it. Shown read-only, the way
      // Folders shows the owner.
      const inherited = directory
        .filter((entry) => isAdmin(entry.role) && !granted.has(entry.userId))
        .map(toPrincipal);

      const inheritedIds = new Set(inherited.map((person) => person.userId));
      const candidates = directory
        .filter(
          (entry) =>
            !granted.has(entry.userId) &&
            !inheritedIds.has(entry.userId) &&
            entry.userId !== context.userId,
        )
        .map(toPrincipal);

      const { data: session } = await supabase.auth.getSession();
      const pendingResponse = context.invitations === false ? null : await fetch(`/api/invitations?resourceType=${encodeURIComponent(resource.resourceType)}&resourceId=${encodeURIComponent(resource.resourceId)}`, {
        headers: { Authorization: `Bearer ${session.session?.access_token ?? ''}` },
        signal,
      });
      const pending = pendingResponse?.ok
        ? ((await pendingResponse.json()) as { invitations?: Array<{ id: string; email: string; level: string; expires_at: string }> }).invitations ?? []
        : [];
      const publicAccess: PublicAccess | undefined = publicDocument?.data
        ? {
            url: editorPathFor(resource.resourceId, node.data?.doc_type),
            isPublic: publicDocument.data.is_public,
            canManage: isAdmin(context.role),
          }
        : undefined;

      return {
        grants,
        inherited,
        candidates,
        pendingInvites: pending.map((row) => ({
          invitationId: row.id,
          email: row.email,
          level: row.level as AccessLevel,
          expiresAt: row.expires_at,
        })),
        // RLS returns only the requests this user may answer.
        accessRequests: (requests.data ?? []).map((row) => ({
          requestId: row.id,
          email: row.requester_email,
          createdAt: row.created_at,
        })),
        canAdd: context.role !== 'viewer',
        ...(publicAccess ? { publicAccess } : {}),
      };
    },

    async add(subject, email, level): Promise<AddOutcome> {
      const resource = assertResource(subject);
      const address = email.trim().toLowerCase();
      if (!address.includes('@')) throw new ShareError('invalidEmail');

      const directory = await loadDirectory(resource.orgId);
      const person = directory.find((entry) => entry.email.toLowerCase() === address);
      if (!person && context.invitations === false) throw new ShareError('noSuchUser');
      if (!person && context.invitations !== false) {
        const { data: session } = await supabase.auth.getSession();
        const token = session.session?.access_token;
        if (!token) throw new ShareError('notAllowed');
        const response = await fetch('/api/invitations', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind: 'resource',
            email: address,
            resourceType: resource.resourceType,
            resourceId: resource.resourceId,
            level,
          }),
        });
        if (!response.ok) {
          if (response.status === 403) throw new ShareError('notAllowed');
          if (response.status === 404) throw new ShareError('notFound');
          throw new ShareError('unknown');
        }
        const result = (await response.json()) as { outcome?: 'granted' | 'invited'; inviteLink?: string };
        return result.outcome === 'granted'
          ? { type: 'granted' }
          : { type: 'invited', ...(result.inviteLink ? { inviteLink: result.inviteLink } : {}) };
      }

      const { data: session } = await supabase.auth.getSession();
      const response = await fetch('/api/invitations', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'resource', email: address, resourceType: resource.resourceType, resourceId: resource.resourceId, level }),
      });
      if (!response.ok) throw new ShareError(response.status === 403 ? 'notAllowed' : response.status === 409 ? 'alreadyHasAccess' : 'unknown');
      const result = (await response.json()) as { outcome?: 'granted' | 'invited'; inviteLink?: string };
      return result.outcome === 'granted'
        ? { type: 'granted' }
        : { type: 'invited', ...(result.inviteLink ? { inviteLink: result.inviteLink } : {}) };
    },

    async changeLevel(subject, userId, level) {
      const resource = assertResource(subject);
      const { data: session } = await supabase.auth.getSession();
      const response = await fetch(`/api/shares/${resource.resourceType}/${resource.resourceId}/${userId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ level }),
      });
      if (!response.ok) throw new ShareError(response.status === 403 ? 'notAllowed' : 'unknown');
    },

    async revoke(subject, userId) {
      const resource = assertResource(subject);
      const { data: session } = await supabase.auth.getSession();
      const response = await fetch(`/api/shares/${resource.resourceType}/${resource.resourceId}/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.session?.access_token ?? ''}` },
      });
      if (!response.ok) throw new ShareError(response.status === 403 ? 'notAllowed' : 'unknown');
    },

    async cancelInvite(_subject, invitationId) {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new ShareError('notAllowed');
      const response = await fetch(`/api/invitations/${encodeURIComponent(invitationId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new ShareError(response.status === 403 ? 'notAllowed' : 'unknown');
    },

    async approveRequest(subject, request: AccessRequest) {
      // A share made by the Worker, exactly as if the email had been typed in.
      // Already having access (granted meanwhile) still answers the request.
      try {
        await this.add(subject, request.email, 'view');
      } catch (cause) {
        if (!(cause instanceof ShareError && cause.code === 'alreadyHasAccess')) throw cause;
      }
      await this.dismissRequest!(subject, request.requestId);
    },

    async dismissRequest(_subject, requestId) {
      const { error } = await supabase.from('access_requests').delete().eq('id', requestId);
      if (error) throw new ShareError('unknown', error.message);
    },

    async setPublic(subject, isPublic) {
      const resource = assertResource(subject);
      const changed = await setSheetPublic(resource.resourceId, isPublic);
      if (!changed) throw new ShareError('notAllowed');
    },
  };
}

